import React from 'react';
import { refundsState, orderAmount } from '../lib/netRevenue';

// Overnight — what came in during the last 24h: new accounts, new cases, new
// orders. Sits under the follow-up queue in the Dashboard's Today column so
// the first screen of the day answers "what arrived and who do I call".
// Reads `overnight` from GET /api/dashboard; renders nothing when the backend
// hasn't shipped it yet (undefined), a one-line note when a Salesforce read
// failed (unavailable). Design: squish-docs/architecture/follow-up-tasks.md.

function fmtTimeAgo(iso) {
  if (!iso) return '—';
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmt$(v) {
  if (v == null) return '—';
  return `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function priorityBadge(p) {
  return ({ High: 'badge-red', Medium: 'badge-yellow', Low: 'badge-green' })[p] || 'badge-gray';
}

function Card({ title, count, children, empty }) {
  return (
    <div className="ov-card">
      <div className="ov-card-head">
        <span>{title}</span>
        <span className="today-section-count">{count}</span>
      </div>
      {count === 0 ? <div className="ov-empty">{empty}</div> : children}
    </div>
  );
}

export default function Overnight({ overnight, onPickCustomer, onNavigateToTicket }) {
  if (!overnight) return null; // backend without overnight yet — say nothing
  const signups = overnight.signups || [];
  const cases = overnight.cases || [];
  const orders = overnight.orders || [];
  const total = signups.length + cases.length + orders.length;
  // An order placed last night and already refunded is not new business. The
  // amount is the order's total less what went back; hovering says what came
  // off. With refunds unavailable it is the plain total, as it always was.
  const refunds = refundsState(overnight);

  return (
    <section className="ov">
      <div className="ov-head">
        <div className="today-title">Overnight</div>
        <div className="today-subtitle">
          {overnight.unavailable
            ? 'partial — a Salesforce read failed'
            : total === 0
              ? 'quiet — nothing new in 24h'
              : `last 24h · ${signups.length} account${signups.length === 1 ? '' : 's'} · ${cases.length} case${cases.length === 1 ? '' : 's'} · ${orders.length} order${orders.length === 1 ? '' : 's'}`}
        </div>
      </div>

      <div className="ov-cards">
        <Card title="New accounts" count={signups.length} empty="None overnight.">
          {signups.map(c => (
            <div key={c.Id} className="ov-item" onClick={() => c.AccountId && onPickCustomer?.({ Id: c.AccountId, Name: c.Account?.Name })}>
              <div className="ov-item-main">
                <div className="ov-item-title" title={c.Name || c.Email || undefined}>{c.Name || c.Email || '—'}</div>
                <div className="ov-item-meta">{c.Email || 'no email'}{c.Account?.Name && c.Account.Name !== c.Name ? ` · ${c.Account.Name}` : ''}</div>
              </div>
              <div className="ov-item-when">{fmtTimeAgo(c.CreatedDate)}</div>
            </div>
          ))}
        </Card>

        <Card title="New cases" count={cases.length} empty="None overnight.">
          {cases.map(k => (
            <div key={k.Id} className="ov-item" onClick={() => onNavigateToTicket?.(k.Id)}>
              <div className="ov-item-main">
                <div className="ov-item-title" title={k.Subject || undefined}>{k.Subject || '—'}</div>
                <div className="ov-item-meta">
                  <span style={{ color: 'var(--accent)' }}>{k.CaseNumber}</span>
                  {k.Account?.Name ? ` · ${k.Account.Name}` : ''}{k.Origin ? ` · ${k.Origin}` : ''}
                </div>
              </div>
              <div className="ov-item-side">
                <span className={`badge ${priorityBadge(k.Priority)}`}>{k.Priority || '—'}</span>
                <span className="ov-item-when">{fmtTimeAgo(k.CreatedDate)}</span>
              </div>
            </div>
          ))}
        </Card>

        <Card title="New orders" count={orders.length} empty="None overnight.">
          {orders.map(o => (
            <div key={o.Id} className="ov-item" onClick={() => o.AccountId && onPickCustomer?.({ Id: o.AccountId, Name: o.Account?.Name })}>
              <div className="ov-item-main">
                <div className="ov-item-title" title={o.Account?.Name || undefined}>{o.Account?.Name || '—'}</div>
                <div className="ov-item-meta">
                  <span style={{ color: 'var(--accent)' }}>{o.OrderNumber}</span>
                  {' · '}{o.OrderItems?.records?.map(i => i.Product2?.Name || '—').join(', ') || 'no lines'}
                </div>
              </div>
              <div className="ov-item-side">
                <span className="ov-amount" title={orderAmount(o, refunds).title}>
                  {fmt$(orderAmount(o, refunds).amount)}{orderAmount(o, refunds).refunded > 0 ? '*' : ''}
                </span>
                <span className="ov-item-when">{fmtTimeAgo(o.CreatedDate || o.EffectiveDate)}</span>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </section>
  );
}
